import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Machine } from "../../utils/models/Machine"
import { useSelector } from "react-redux"
import { selectUser } from "../../utils/redux/userSlice"
import pdfMake from "pdfmake/build/pdfmake"
import { Content, TDocumentDefinitions } from "pdfmake/interfaces"
import "chartjs-adapter-date-fns"

import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Legend,
} from "chart.js"
ChartJS.register(
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Legend
)

import { vfs as pdfFontsVfs } from "pdfmake/build/vfs_fonts"
;(pdfMake as any).vfs = pdfFontsVfs

import Chart from "chart.js/auto"
import {
    useLineChart,
    usePieChartImage,
    useBarChartImage
} from "./GeneratePDFCharts"

function renderChartImage(
    data: any,
    options: any = {},
    width = 800,
    height = 500
) {
    const canvas = document.createElement("canvas")
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext("2d")!
    const chart = new Chart(ctx, {
        type: "line",
        data,
        options: { responsive: false, animation: false, ...options }
    })
    const img = canvas.toDataURL("image/png")
    chart.destroy()
    return img
}

type AlarmType = "Minor" | "Warning" | "Critical" | "No Alarms"
interface AlarmData {
    name: AlarmType
    value: number
}

const ReportingPdfDownload = (props: {
    machine: Machine
    date: string
    itemsChar: string
    profitsChar: string
    alarmsChart: string
    errorsMonth: number
    errorsTotal: number
    productsMonth: number
    productsTotal: number
    productivityMonth: number
    productivityTotal: number
    itemsPerDay: { date: string; numItems: number }[]
}) => {
    const { t } = useTranslation()
    const user = useSelector(selectUser)
    const chartRef = useRef<any>(null)

    const [img2, setImg2] = useState<string>()
    const i = useLineChart()
    useEffect(() => {
        setImg2(i)
    }, [i])

    const ALARM_COLORS: Record<AlarmType, string> = {
        Minor: "#FBDB59",
        Warning: "#EAA537",
        Critical: "#CF594D",
        "No Alarms": "#A8AAA8"
    }

    const [alarmData, setAlarmData] = useState<AlarmData[]>([])
    useEffect(() => {
        setAlarmData([
            { name: "Minor", value: 12 },
            { name: "Warning", value: 8 },
            { name: "Critical", value: 3 },
            { name: "No Alarms", value: 27 }
        ])
    }, [])

    const pieImg = usePieChartImage(alarmData, ALARM_COLORS, {
        width: 500,
        height: 500,
        doughnut: false
    })

    const img = renderChartImage({
        labels: ["Gen", "Feb", "Mar"],
        datasets: [
            {
                label: "Producció",
                data: [12, 19, 9],
                borderColor: "#2563eb",
                tension: 0.3
            }
        ]
    })

    const barLabels = props.itemsPerDay.map((d) => d.date)
    const barSeries = [
        {
            label: t("Items"),
            data: props.itemsPerDay.map((d) => d.numItems),
            backgroundColor: "#2563eb"
        }
    ]
    const barsImg = useBarChartImage(barLabels, barSeries, {
        width: 900,
        height: 350,
        rounded: true,
        stacked: false,
        horizontal: false,
        showLegend: false
    })

    // PDF
    const downloadPdf = () => {
        const publicLogo = new URL(
            "/logo_small.png",
            window.location.origin
        ).toString()
        const tableLayouts = {
            softGray: {
                hLineWidth: () => 0.5,
                vLineWidth: () => 0.5,
                hLineColor: () => "#e6e6e6",
                vLineColor: () => "#e6e6e6",
                paddingLeft: () => 8,
                paddingRight: () => 8,
                paddingTop: () => 6,
                paddingBottom: () => 6
            }
        }

        const docDefinition: TDocumentDefinitions = {
            pageMargins: [40, 90, 40, 60],

            images: {
                logo: publicLogo,
                ...(img ? { chart: img } : {}),
                ...(img2 ? { chart2: img2 } : {}),
                ...(pieImg ? { chart3: pieImg } : {}),
                ...(barsImg ? { chartBars: barsImg } : {}) // ✅ bar chart
            },

            header: {
                margin: [40, 20, 40, 10],
                fontSize: 10,
                stack: [
                    {
                        columns: [
                            { image: "logo", width: 140, alignment: "left",  },
                            {
                                stack: [
                                    `${user.company.name}`,
                                    `${props.machine.instalation.name}`,
                                    `${props.machine.instalation.city}`,
                                    `${props.machine.instalation.address}`
                                ],
                                alignment: "right",
                                color: "#808080"
                            }
                        ]
                    },
                    {
                        canvas: [
                            {
                                type: "line",
                                x1: 0,
                                y1: 0,
                                x2: 515,
                                y2: 0,
                                lineWidth: 1,
                                lineColor: "#cccccc"
                            }
                        ],
                        margin: [0, 10, 0, 0]
                    }
                ]
            },

            content: [
                { text: t("MachineReport"), style: "title" },
                { text: `${t("Client")}: ${user.company.name}` },
                { text: `${t("Machine")}: ${props.machine.name}` },
                { text: `${t("Date")}: ${props.date}` },

                { text: t("GlobalIndicators"), style: "header" },
                {
                    table: {
                        headerRows: 1,
                        widths: ["*", 100, 100],
                        body: [
                            [
                                { text: "", style: "tableHeader" },
                                { text: t("Total"), style: "tableHeader" },
                                { text: t("Monthly"), style: "tableHeader" }
                            ],
                            [t("Errors"), props.errorsTotal, props.errorsMonth],
                            [
                                t("Items"),
                                props.productsTotal,
                                props.errorsMonth 
                            ],
                            [
                                t("Productivity"),
                                `${props.productivityTotal} %`,
                                `${props.productivityMonth} %`
                            ]
                        ]
                    },
                    layout: "softGray"
                },

                { text: t("ItemsForDay"), style: "header" },
                ...(barsImg
                    ? [
                          {
                              image: "chartBars",
                              width: 500,
                              alignment: "center"
                          } as Content
                      ]
                    : []),

                { text: t("Profits"), style: "header" },
                { image: "chart2", width: 500 },

                { text: t("Alarms"), style: "header" },
                ...(pieImg
                    ? [
                          {
                              image: "chart3",
                              width: 300,
                              alignment: "center"
                          } as Content
                      ]
                    : [])
            ],

            styles: {
                header: {
                    fontSize: 16,
                    bold: true,
                    marginBottom: 5,
                    marginTop: 10
                },
                title: {
                    fontSize: 22,
                    bold: true,
                    marginBottom: 10
                },
                tableHeader: {
                    fillColor: "#e6e6e6",
                    color: "#333",
                }
            },

            footer: (currentPage: number, pageCount: number): Content => ({
                text: `${currentPage} / ${pageCount}`,
                alignment: "right",
                margin: [0, 20, 40, 0],
                fontSize: 9,
                color: "#808080"
            })
        }

        pdfMake.createPdf(docDefinition, tableLayouts).download(`${props.machine.name} - ${props.date}.pdf`)
    }

    return (
            <button className='btn-Custom btn-IDGrey' onClick={downloadPdf}><i className="bi bi-file-earmark-arrow-down" style={{color:"var(--text-primary"}}></i></button>
    )
}

export default ReportingPdfDownload
